import { configureStore } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';

import {
  getNotificationState,
  setNotificationState,
  resetNotificationState,
} from '~/state/slices/notificationSlice';
import { getUserState, setUserState, resetUserState } from '~/state/slices/userSlice';
import {
  getActingAsState,
  setActingAsState,
  resetActingAsState,
} from '~/state/slices/actingAsSlice';

import reducers from '~/state/reducers';
import type { RootState } from '~/state/reducers';
import { toolboxApi } from '~/modules/query/base';
import { aiApi } from '~/modules/query/ai';
import { notificationApi } from '~/modules/query/notification';
import { loadPersistedState, savePersistedState } from '~/state/persist';

const preloadedState = loadPersistedState();

const store = configureStore({
  devTools: true,
  reducer: reducers,
  preloadedState: preloadedState,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      toolboxApi.middleware,
      aiApi.middleware,
      notificationApi.middleware,
    ),
});

export type { RootState };
export type AppDispatch = typeof store.dispatch;

// Typed counterpart of the raw react-redux hook on `globalState`.
export const useAppDispatch = () => useDispatch<AppDispatch>();

store.subscribe(() => {
  savePersistedState(store.getState());
});

const globalState = {
  //redux
  store,
  //notifications
  useNotificationState: () => {
    const notificationState = useSelector(getNotificationState);

    return notificationState;
  },
  setNotificationState,
  resetNotificationState,
  //user
  useUserState: () => {
    const userState = useSelector(getUserState);

    return userState;
  },
  setUserState,
  resetUserState,
  //Acting As
  useActingAsState: () => {
    const actingAsState = useSelector(getActingAsState);

    return actingAsState;
  },
  setActingAsState,
  resetActingAsState,
};

export default globalState;
